import React, { useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import { useForm } from "react-hook-form";
import Audit from "src/components/Audit";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import RACI from "src/components/RACI";
import Select from "src/components/Select";
import Tabs from "src/components/Tabs";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { IModalAddOrChangeKanban, IRefKanbanModal, IKanban, IKanbanAssociations } from "./interface";
import addOrChangeSchema from "./schemas";


const ModalAddChangeDeleteKanban: React.ForwardRefRenderFunction<IRefKanbanModal, IModalAddOrChangeKanban> = ({ 
	onCallbackChange,
	onCallbackDelete
}, ref) => {

	const { translate } = useTranslation();
	const { confirm } = useConfirmModal();
	const { addToast } = RCCHookToast();

	const [selectedKanban, setSelectedKanban] = useState<IKanban>({} as IKanban);
	const [isAdd, setIsAdd] = useState(false);

	const onCallbackAddFunction = useRef<Function>(() => {});
	const refModalAddOrChangeKanban = useRef<RCCRefModalProps>(null);

	const { 
		handleSubmit, 
		register, 
		reset,
		setError, 
		unregister,
		control,
		formState: {errors},
		clearErrors 
	} = useForm();

	const actionButtonAdd = (onCallback?: Function ) => {
		reset();
		setIsAdd(true);
		if(onCallback)
			onCallbackAddFunction.current = onCallback;
		setSelectedKanban({} as IKanban);
		refModalAddOrChangeKanban.current?.openModal();
	};

	const actionButtonChange = (kanbanId: number) => {
		reset();
		setIsAdd(false);
		queryKanbanData(kanbanId);
	};
	
	const actionButtonDelete = (idKanban: number, kanbanDescription: string) => {
		confirm(
			translate("Exclusão de Kanban"), 
			`${translate("Deseja realmente excluir o kanban")}: ${kanbanDescription}?`,
			async () => {

				await RCCServicesApi.deleteRequest({
					method: "Kanban/Delete",
					urlParameters: {
						idKanban
					}
				});

				addToast({
					color: "green",
					description: translate("Kanban excluído com sucesso"),
					title: translate("Sucesso")
				});
	
				if(onCallbackDelete)
					onCallbackDelete(idKanban);

			},
			(err: any) => {
				const textMessageError = err?.response?.data?.Message;
				if(textMessageError === "hasLinkedActions"){
					addToast({
						title: translate("Erro"),
						description: translate("Este kanban possui ações vinculadas"),
						color: "yellow"
					});
					return;
				}
				standardErrorMessages(err, translate("Erro ao excluir kanban"), addToast);
			}
		);
	};

	const queryKanbanData = async (idKanban: number) => {
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Kanban/Select",
				urlParameters: {
					idKanban
				}
			});

			setSelectedKanban(data);
			refModalAddOrChangeKanban.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar kanban"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	const objToInt = (obj: any) => {
		if(typeof obj === "object")
			return parseInt(obj.value, 10);
		if(typeof obj === "string" && obj !== "")
			return parseInt(obj, 10);
		if(typeof obj === "string" && obj === "")
			return undefined;
		if(typeof obj !== "number")
			return undefined;
		return obj;
	};

	const onSubmit = async (data: any) => {
		try{
			clearErrors();

			const dataChanged = data;
			dataChanged.kba001Efa005Id = objToInt(data.kba001Efa005Id);
			dataChanged.kba001Efa005Id2 = objToInt(data.kba001Efa005Id2);

			dataChanged.kbtba003 = data.kbtba003?.filter((e: any) => e.kba003Efa005Id || e.kba003Efa004Id);

			await addOrChangeSchema(translate).validate({...data},{
				abortEarly: false
			});

			if(isAdd){
				const response = await RCCServicesApi.postRequest({
					method: "Kanban/Insert",
					body: {
						...data,
						kba001Ativo: data.kba001Ativo ? "S": "N",
						kba001Efa005Id: data.kba001Efa005Id ? data.kba001Efa005Id : "",
						kba001Efa004Id: data.kba001Efa004Id ? data.kba001Efa004Id : "",
						kba001Tipo: data.kba001Tipo ? data.kba001Tipo : IKanbanAssociations.ACTIONS
					}
				});

				if(onCallbackAddFunction?.current)
					onCallbackAddFunction.current(response.data);

			}else{
				await RCCServicesApi.postRequest({
					method: "Kanban/Update",
					body: {
						...data,
						kba001Id: selectedKanban.kba001Id,
						kba001Ativo: data.kba001Ativo ? "S": "N",
						kba001Efa005Id: data.kba001Efa005Id ? data.kba001Efa005Id : "",
						kba001Efa004Id: data.kba001Efa004Id ? data.kba001Efa004Id : ""
					}
				});

				if(onCallbackChange)
					onCallbackChange();
			}

			refModalAddOrChangeKanban.current?.closeModal();

		}catch(err){
			refModalAddOrChangeKanban.current?.resetLoadingButton();
			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar kanban") : translate("Erro ao alterar kanban"), addToast);
			refModalAddOrChangeKanban.current?.closeModal();
		}
	};

	return (
		<>
			<Modal
				ref={refModalAddOrChangeKanban}
				onSubmit={handleSubmit(onSubmit)}
				header={{
					title: isAdd ? translate("Adicionar kanban"): translate("Alterar kanban"),
					subtitle: !isAdd ? `${translate("Kanban")}: ${selectedKanban.kba001Descricao}`: "",
					icon: {
						name: isAdd? "plus": "pencil"
					}
				}}
				styleInline={ModalSide}
			>
				<Tabs
					name="kanbanTab"
					tabInit={0}
					options={
						[
							{
								label: translate("Dados"),
								content: (
									<>
										<RCCGrid container item 
											xs={12} 
										>
											<RCCGrid item>
												<Audit data={selectedKanban} type="pop"/>
											</RCCGrid>
											<RCCGrid container item>		
												<RCCInput 
													name="kba001Descricao"
													register={register}
													label={translate("Descrição")}
													defaultValue={selectedKanban.kba001Descricao ?? ""}
													error={errors?.kba001Descricao}
													maxLength={200}
													required
												/>
											</RCCGrid>
											{isAdd && (
												<RCCGrid container item>
													<Select 
														control={control}
														name="kba001Tipo"
														label={translate("Tipo")}
														options={[
															{
																value: IKanbanAssociations.ACTIONS.toString(),
																label: translate("Ações")
															},
															{
																value: IKanbanAssociations.PROJECTS.toString(),
																label: translate("Projetos")
															}
														]}
														required
														defaultValue={IKanbanAssociations.ACTIONS.toString()}
													/>	
												</RCCGrid>	
											)}	
											<RCCGrid container item>		
												<RCCSwitch
													name="kba001Ativo"
													label={translate("Ativo")}
													register={register}
													defaultChecked={selectedKanban.kba001Ativo === "S" || isAdd}
												/>
											</RCCGrid>
										</RCCGrid>
									</>
								)
							},
							{
								label: translate("RACI"),
								content: (
									<>
										<RACI 
											accountable={{
												nameOfSelect: "kba001Efa005Id2",
												defaultValue: {
													select: selectedKanban.kba001Efa005Id2 ? selectedKanban.kba001Efa005Id2.toString() : "",
													checkbox: "N"
												}
											}}
											noCheckbox
											control={control}
											register={register}
											unregister={unregister}
											clearErrors={clearErrors}
											error={errors}
											participants={{
												nameOfObject: "kbtba003",
												nameOfID: "kba003Id",
												nameOfCheckbox: "kba003Notificaparticipante",
												nameOfSelectGroup: "kba003Efa004Id",
												nameOfSelectGroupDescription: "kba003Efa004Nome",
												nameOfSelectUser: "kba003Efa005Id",
												nameOfSelectUserDescription: "kba003Efa005Nome",
												nameOfType: "kba003Tipo",
												defaultValue: selectedKanban.kbtba003
											}}
											responsible={{
												nameOfSelect: "kba001Efa005Id",
												defaultValue: {
													select: selectedKanban.kba001Efa005Id ? selectedKanban.kba001Efa005Id.toString() : "",
													checkbox: "N"
												}
											}}
										/>
									</>
								)
							}
						]
					}
				/>
			</Modal>
		
		</>
	);
};


export default React.forwardRef(ModalAddChangeDeleteKanban);
